// src/utils/callableNativeSafe.js
// LOOP CALLABLE TRANSPORT R6.3
//
// Web/Android:
// - Uses the normal Firebase callable SDK.
// - Explicitly waits for a valid App Check token when requireAppCheck=true.
// - Retries once with a refreshed App Check token when a public callable
//   returns unauthenticated because App Check was missing or stale.
//
// iOS Capacitor:
// - Sends the official https.onCall protocol through CapacitorHttp/URLSession.
// - Adds Authorization only when authentication is required.
// - Adds X-Firebase-AppCheck when App Check is required.
//
// Backwards-compatible defaults:
// - Authentication is required.
// - Explicit App Check is not required.
//
// Public App Check protected callables can opt into:
// - requireAuth: false
// - requireAppCheck: true

import { getFunctions, httpsCallable } from "firebase/functions";
import { getCurrentAuthIdToken } from "../auth/socialAuth";
import {
  getLoopAppCheckStatus,
  getLoopAppCheckToken,
} from "../appCheckBootstrap";
import { httpRequestJson, isIosCapacitor } from "./nativeHttp";

const LOOP_FUNCTIONS_BASE =
  "https://us-central1-web-app-d9799.cloudfunctions.net";

function withTimeout(promise, ms, label = "callable-timeout") {
  let timer = null;

  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const error = new Error(label);
      error.code = "deadline-exceeded";
      error.details = {
        reason: label,
      };
      reject(error);
    }, ms);
  });

  return Promise.race([
    Promise.resolve(promise).finally(() => {
      try {
        if (timer) clearTimeout(timer);
      } catch (_) {}
    }),
    timeout,
  ]);
}

function makeClientCallableError({
  message,
  code = "internal",
  reason = "client-callable-error",
  originalError = null,
  extraDetails = null,
}) {
  const error = new Error(
    message || "La función no pudo completar la solicitud.",
  );

  error.code = String(code || "internal")
    .replace(/^functions\//i, "")
    .toLowerCase();

  error.details = {
    reason,
    ...(extraDetails && typeof extraDetails === "object"
      ? extraDetails
      : {}),
  };

  if (originalError) {
    error.customData = {
      originalCode: originalError?.code || null,
      originalMessage:
        originalError?.message || String(originalError),
      originalDetails:
        originalError?.details || null,
    };
  }

  return error;
}

function callableProtocolError(response = {}) {
  const serverError = response?.error;
  if (!serverError) return null;

  const error = new Error(
    serverError.message ||
      "La función no pudo completar la solicitud.",
  );

  error.code = String(
    serverError.status ||
      serverError.code ||
      "internal",
  )
    .replace(/^functions\//i, "")
    .toLowerCase();

  error.details = serverError.details || null;
  error.serverResponse = response;

  return error;
}

function callableErrorText(error) {
  let details = "";

  try {
    details = JSON.stringify(
      error?.details ||
      error?.customData ||
      error?.serverResponse ||
      {},
    );
  } catch (_) {}

  return [
    error?.code,
    error?.message,
    details,
    error,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isAuthenticationFailure(error) {
  const raw = callableErrorText(error);

  return (
    raw.includes("unauthenticated") ||
    raw.includes("http 401") ||
    raw.includes("auth-token-missing") ||
    raw.includes("token expired") ||
    raw.includes("id token has expired")
  );
}

function isAppCheckFailure(error) {
  const raw = callableErrorText(error);

  return (
    raw.includes("app check") ||
    raw.includes("app-check") ||
    raw.includes("appcheck") ||
    raw.includes("firebase-app-check") ||
    raw.includes("x-firebase-appcheck") ||
    raw.includes("debug-token-not-registered") ||
    raw.includes("provider-not-ready") ||
    raw.includes("provider-disabled") ||
    raw.includes("fetch-status-error") ||
    raw.includes("app-check-token-missing")
  );
}

function isPublicCallableAppCheckRejection(
  error,
  requireAuth,
  requireAppCheck,
) {
  return Boolean(
    requireAppCheck &&
    (
      isAppCheckFailure(error) ||
      (!requireAuth && isAuthenticationFailure(error))
    ),
  );
}

async function requireValidAppCheckToken({
  functionName,
  forceRefresh = false,
}) {
  try {
    const token = await withTimeout(
      getLoopAppCheckToken(Boolean(forceRefresh)),
      15000,
      `${functionName}-app-check-token-timeout`,
    );

    if (!token) {
      throw makeClientCallableError({
        message:
          "No pudimos obtener la validación de App Check.",
        code: "unauthenticated",
        reason: "app-check-token-missing",
        extraDetails: {
          appCheckStatus:
            getLoopAppCheckStatus(),
        },
      });
    }

    return token;
  } catch (error) {
    if (
      error?.details?.reason ===
        "app-check-token-missing" ||
      isAppCheckFailure(error)
    ) {
      if (!error.code) {
        error.code = "unauthenticated";
      }

      error.details = {
        ...(error?.details &&
        typeof error.details === "object"
          ? error.details
          : {}),
        reason:
          error?.details?.reason ||
          "app-check-token-unavailable",
        appCheckStatus:
          getLoopAppCheckStatus(),
      };

      throw error;
    }

    throw makeClientCallableError({
      message:
        "No pudimos validar App Check para esta solicitud.",
      code: "unauthenticated",
      reason: "app-check-token-unavailable",
      originalError: error,
      extraDetails: {
        appCheckStatus:
          getLoopAppCheckStatus(),
      },
    });
  }
}

async function callIosCallableProtocol({
  functionName,
  data,
  timeoutMs,
  requireAuth,
  requireAppCheck,
  forceRefreshAuth = false,
  forceRefreshAppCheck = false,
}) {
  let idToken = null;
  let appCheckToken = null;

  if (requireAuth) {
    idToken = await withTimeout(
      getCurrentAuthIdToken({
        forceRefresh: forceRefreshAuth,
      }),
      15000,
      `${functionName}-auth-token-timeout`,
    );

    if (!idToken) {
      throw makeClientCallableError({
        message:
          "No pudimos obtener la sesión actual.",
        code: "unauthenticated",
        reason: "auth-token-missing",
      });
    }
  }

  if (requireAppCheck) {
    appCheckToken =
      await requireValidAppCheckToken({
        functionName,
        forceRefresh:
          forceRefreshAppCheck,
      });
  }

  const headers = {};

  if (idToken) {
    headers.Authorization = `Bearer ${idToken}`;
  }

  if (appCheckToken) {
    headers["X-Firebase-AppCheck"] =
      appCheckToken;
  }

  const response = await withTimeout(
    httpRequestJson({
      url:
        `${LOOP_FUNCTIONS_BASE}/` +
        encodeURIComponent(functionName),
      method: "POST",
      headers,
      // Official Firebase https.onCall protocol:
      // the request body may only contain data.
      data: {
        data: data ?? null,
      },
    }),
    timeoutMs,
    `${functionName}-native-callable-timeout`,
  );

  const protocolError =
    callableProtocolError(response);

  if (protocolError) {
    throw protocolError;
  }

  const result =
    response?.result ??
    response?.data ??
    response?.response ??
    null;

  if (
    result === null ||
    result === undefined
  ) {
    throw makeClientCallableError({
      message:
        `${functionName}-invalid-callable-response`,
      code: "internal",
      reason:
        "invalid-callable-response",
      extraDetails: {
        response,
      },
    });
  }

  return {
    data: result,
    transport:
      "ios-native-callable-protocol",
  };
}

async function callPublicAppCheckCallableProtocol({
  functionName,
  data,
  timeoutMs,
  forceRefreshAppCheck = false,
}) {
  const appCheckToken =
    await requireValidAppCheckToken({
      functionName,
      forceRefresh:
        forceRefreshAppCheck,
    });

  const url =
    `${LOOP_FUNCTIONS_BASE}/` +
    encodeURIComponent(functionName);

  let response = null;

  try {
    response = await withTimeout(
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type":
            "application/json",
          "X-Firebase-AppCheck":
            appCheckToken,
        },
        body: JSON.stringify({
          data: data ?? null,
        }),
        credentials: "omit",
      }),
      timeoutMs,
      `${functionName}-web-direct-callable-timeout`,
    );
  } catch (error) {
    throw makeClientCallableError({
      message:
        "No pudimos conectar con la función de reserva.",
      code:
        error?.code ||
        "unavailable",
      reason:
        "web-direct-callable-network-error",
      originalError: error,
      extraDetails: {
        functionName,
        url,
      },
    });
  }

  let rawText = "";
  let payload = null;

  try {
    rawText = await response.text();

    if (rawText) {
      payload = JSON.parse(rawText);
    }
  } catch (error) {
    throw makeClientCallableError({
      message:
        "La función devolvió una respuesta que no pudimos leer.",
      code: "internal",
      reason:
        "web-direct-callable-invalid-json",
      originalError: error,
      extraDetails: {
        functionName,
        httpStatus:
          response?.status || null,
        responsePreview:
          String(rawText || "").slice(0, 500),
      },
    });
  }

  const protocolError =
    callableProtocolError(payload || {});

  if (protocolError) {
    throw protocolError;
  }

  if (!response.ok) {
    throw makeClientCallableError({
      message:
        `La función respondió HTTP ${response.status}.`,
      code:
        response.status === 401
          ? "unauthenticated"
          : response.status === 403
            ? "permission-denied"
            : response.status === 404
              ? "not-found"
              : response.status >= 500
                ? "unavailable"
                : "internal",
      reason:
        "web-direct-callable-http-error",
      extraDetails: {
        functionName,
        httpStatus:
          response.status,
        httpStatusText:
          response.statusText || null,
        responsePreview:
          String(rawText || "").slice(0, 500),
      },
    });
  }

  const result =
    payload?.result ??
    payload?.data ??
    payload?.response ??
    null;

  if (
    result === null ||
    result === undefined
  ) {
    throw makeClientCallableError({
      message:
        `${functionName}-invalid-callable-response`,
      code: "internal",
      reason:
        "web-direct-invalid-callable-response",
      extraDetails: {
        functionName,
        payload,
      },
    });
  }

  return {
    data: result,
    transport:
      "web-direct-public-app-check-callable-protocol",
  };
}

async function callWebOrAndroidCallableSdk({
  functionName,
  data,
  timeoutMs,
  requireAppCheck,
  forceRefreshAppCheck = false,
}) {
  if (requireAppCheck) {
    // httpsCallable attaches the token automatically, but requesting one first
    // removes the initialization race and produces an actionable local error.
    await requireValidAppCheckToken({
      functionName,
      forceRefresh:
        forceRefreshAppCheck,
    });
  }

  const callable = httpsCallable(
    getFunctions(),
    functionName,
  );

  return withTimeout(
    callable(data),
    timeoutMs,
    `${functionName}-sdk-callable-timeout`,
  );
}

export function getCallableRuntimeDiagnostics() {
  let appCheck = {};

  try {
    appCheck = getLoopAppCheckStatus() || {};
  } catch (_) {
    appCheck = {};
  }

  const platform =
    String(appCheck.platform || "").toLowerCase() ||
    (isIosCapacitor() ? "ios" : "web");

  const normalizedPlatform =
    ["ios", "android", "web"].includes(platform)
      ? platform
      : "unknown";

  return {
    platform: normalizedPlatform,
    runtime:
      normalizedPlatform === "ios"
        ? "capacitor-ios-native-callable"
        : normalizedPlatform === "android"
          ? "capacitor-android-firebase-sdk"
          : "web-firebase-sdk",
    appCheckProvider:
      String(appCheck.provider || "").slice(0, 80) || null,
    appCheckReady: Boolean(
      appCheck.enabled === true &&
      (appCheck.nativeInitialized === true ||
        appCheck.webInitialized === true),
    ),
    online:
      typeof navigator === "undefined"
        ? true
        : navigator.onLine !== false,
    visibility:
      typeof document === "undefined"
        ? null
        : String(document.visibilityState || "").slice(0, 24) || null,
  };
}

export async function callCallableNativeSafe({
  functionName,
  data = null,
  timeoutMs = 45000,
  requireAuth = true,
  requireAppCheck = false,
} = {}) {
  const cleanName =
    String(functionName || "").trim();

  if (!cleanName) {
    throw makeClientCallableError({
      message:
        "Falta el nombre de la función.",
      code: "invalid-argument",
      reason:
        "callable-function-name-required",
    });
  }

  const authRequired =
    requireAuth !== false;

  const appCheckRequired =
    requireAppCheck === true;

  if (isIosCapacitor()) {
    try {
      return await callIosCallableProtocol({
        functionName: cleanName,
        data,
        timeoutMs,
        requireAuth: authRequired,
        requireAppCheck:
          appCheckRequired,
        forceRefreshAuth: false,
        forceRefreshAppCheck: false,
      });
    } catch (error) {
      const refreshAuth =
        authRequired &&
        isAuthenticationFailure(error);

      const refreshAppCheck =
        isPublicCallableAppCheckRejection(
          error,
          authRequired,
          appCheckRequired,
        );

      if (
        !refreshAuth &&
        !refreshAppCheck
      ) {
        throw error;
      }

      return callIosCallableProtocol({
        functionName: cleanName,
        data,
        timeoutMs,
        requireAuth: authRequired,
        requireAppCheck:
          appCheckRequired,
        forceRefreshAuth: refreshAuth,
        forceRefreshAppCheck:
          refreshAppCheck,
      });
    }
  }

  if (
    !authRequired &&
    appCheckRequired
  ) {
    try {
      return await callPublicAppCheckCallableProtocol({
        functionName: cleanName,
        data,
        timeoutMs,
        forceRefreshAppCheck: false,
      });
    } catch (error) {
      const refreshAppCheck =
        isPublicCallableAppCheckRejection(
          error,
          authRequired,
          appCheckRequired,
        );

      if (!refreshAppCheck) {
        throw error;
      }

      return callPublicAppCheckCallableProtocol({
        functionName: cleanName,
        data,
        timeoutMs,
        forceRefreshAppCheck: true,
      });
    }
  }

  try {
    return await callWebOrAndroidCallableSdk({
      functionName: cleanName,
      data,
      timeoutMs,
      requireAppCheck:
        appCheckRequired,
      forceRefreshAppCheck: false,
    });
  } catch (error) {
    const refreshAppCheck =
      isPublicCallableAppCheckRejection(
        error,
        authRequired,
        appCheckRequired,
      );

    if (!refreshAppCheck) {
      throw error;
    }

    try {
      return await callWebOrAndroidCallableSdk({
        functionName: cleanName,
        data,
        timeoutMs,
        requireAppCheck:
          appCheckRequired,
        forceRefreshAppCheck: true,
      });
    } catch (retryError) {
      if (
        !authRequired &&
        isAuthenticationFailure(retryError)
      ) {
        retryError.details = {
          ...(retryError?.details &&
          typeof retryError.details === "object"
            ? retryError.details
            : {}),
          reason:
            retryError?.details?.reason ||
            "guest-app-check-rejected",
          appCheckStatus:
            getLoopAppCheckStatus(),
        };
      }

      throw retryError;
    }
  }
}
